import type { AnalyticsListOptions } from '../../../../../analytics/analyticsHandler';
import type { ApiProduct } from '../../../../../models/ecommerce/product/ApiProduct';
import type { CartItem } from '../../../api/cart/responses/cartResponses';
import type { GaEcommerceItem } from '../shared/ga4Shared';
import type {
    AddToCartEvent,
    AddToWishlistEvent,
    AssortmentChangeInteraction,
    CartRecipeSubstituteTriggerClick,
    HighAmountInCartImpressionEvent,
    MiniCartRecipeAccordionClick,
    RemoveFromCartEvent,
    RemoveFromCartType,
    ViewCartEvent,
} from './ga4Cart';

import { failSilently } from '../../utils/failSilently';
import { REPLACED_ITEM_LIST_NAME } from '../items';
import {
    getGaChangedItems,
    getGaMinimalProductKey,
    getGaProductsWithVariantsList,
    getGaProductsWithVariantsMap,
    getGaTotalProductsValue,
    mapCartItemToGaEcommerceItem,
    mapProductToGaEcommerceItem,
    type GaEcommerceContextOptions,
} from '../items/ga4Items.utils';
import { mapGaList } from '../shared/ga4SharedMappers';
import { sendGA4Event } from '../shared/sendGa4Event';
import { displayMergeOptions, mergeCartInteraction } from './ga4MergeCart';

const viewCart = (cartItems: CartItem[], totalValue: number | undefined) => {
    const mappedItems = cartItems.map(({ cartItemData }) =>
        mapCartItemToGaEcommerceItem(cartItemData),
    );

    const event: ViewCartEvent = {
        event: 'view_cart',
        ecommerce: {
            currency: 'SEK',
            value: totalValue,
            items: mappedItems,
        },
    };

    sendGA4Event(event);
};

const addToWishlist = async (
    products: { productId: string; variantId: string | undefined; quantity: number }[],
    storeId: string,
    type: AddToWishlistEvent['type'],
    isUserMember: boolean,
) => {
    const options: GaEcommerceContextOptions = { isUserMember };
    const productsWithVariantsAndQuantity = await getGaProductsWithVariantsList(products, storeId);
    const mappedItems = productsWithVariantsAndQuantity.map(({ productData, inputData }) =>
        mapProductToGaEcommerceItem({
            product: productData,
            quantity: inputData.quantity,
            options,
            variantId: inputData.variantId,
        }),
    );

    const event: AddToWishlistEvent = {
        event: 'add_to_wishlist',
        type,
        ecommerce: {
            items: mappedItems,
        },
    };

    sendGA4Event(event);
};

/**
 * @param label - “proceed” = proceeds with the change | 'close' = closes the warning prompt in any way without changing assortment.
 * @description Whenever the user is prompted with the warning when trying to change the assortment, push an interaction event with the following values.
 */
const assortmentChangeWarning = (label: 'proceed' | 'close') => {
    const event: AssortmentChangeInteraction = {
        event: 'interaction',
        eventCategory: 'Ecommerce',
        eventAction: 'assortment change warning',
        eventLabel: label,
    };

    sendGA4Event(event);
};

const addProductItems =
    (analyticsOptions: ModifyCartAnalyticsOptions) =>
    (
        items: (ApiProduct & {
            quantity: number;
            selectedVariantId?: string;
            index?: number;
            replacedItem?: string;
        })[],
    ) => {
        const list =
            (analyticsOptions?.list && mapGaList(analyticsOptions.list, analyticsOptions.listId)) ||
            undefined;
        const options: GaEcommerceContextOptions = {
            isUserMember: analyticsOptions.isUserMember,
        };
        const mappedItems = items.map((item) => ({
            ...mapProductToGaEcommerceItem({
                product: item,
                quantity: item.quantity,
                options,
                variantId: item.selectedVariantId,
                index: item.index,
            }),
            item_list_id: list?.item_list_id,
            item_list_name: list?.item_list_name,
            replaced_item: item.replacedItem,
        }));

        const event: AddToCartEvent = {
            event: 'add_to_cart',
            ecommerce: {
                currency: 'SEK',
                value: getGaTotalProductsValue(mappedItems),
                recipe_list_id: analyticsOptions?.recipe_list_id,
                recipe_list_name: analyticsOptions?.recipe_list_name,
                recipe_type: analyticsOptions?.recipe_type,
                recipe_origin: analyticsOptions?.recipe_origin,
                order_status: analyticsOptions?.order_status,
                items: mappedItems,
            },
        };

        sendGA4Event(event);
    };

const removeProductItems = (
    items: {
        product?: ApiProduct;
        quantity: number;
        productId: string;
        selectedVariantId?: string;
        replacedItem?: boolean;
    }[],
    analyticsOptions: ModifyCartAnalyticsOptions,
) => {
    const list =
        (analyticsOptions?.list && mapGaList(analyticsOptions.list, analyticsOptions.listId)) ||
        undefined;

    const options: GaEcommerceContextOptions = {
        isUserMember: analyticsOptions.isUserMember,
    };
    const mappedItems = items.map<GaEcommerceItem>((item) => {
        let gaItem: GaEcommerceItem = {
            item_id: item.productId,
            price: null,
            quantity: item.quantity,
        };

        if (item.product) {
            gaItem = mapProductToGaEcommerceItem({
                product: item.product,
                quantity: item.quantity,
                options,
                variantId: item.selectedVariantId,
            });
        }

        return {
            ...gaItem,
            item_id: item.productId,
            quantity: item.quantity,
            item_list_id: list?.item_list_id,
            item_list_name: list?.item_list_name,
            replaced_item: item.replacedItem,
        };
    });

    const event: RemoveFromCartEvent = {
        event: 'remove_from_cart',
        type: analyticsOptions?.removalActionType || 'remove',
        ecommerce: {
            currency: 'SEK',
            value: getGaTotalProductsValue(mappedItems),
            items: mappedItems,
        },
    };
    sendGA4Event(event);
};

const replacedItemsAddItems = (
    _products: {
        productId: string;
        variantId: string | undefined;
        quantity: number;
        replacementProductIndex: number;
        replacedProductId: string;
    }[],
    storeId: string,
    isUserMember: boolean,
) =>
    modifyCartItems(
        _products.map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            previousQuantity: 0,
            newQuantity: item.quantity,
            index: item.replacementProductIndex,
            replacedItem: item.replacedProductId,
        })),
        storeId,
        { list: REPLACED_ITEM_LIST_NAME, isUserMember },
    );

const replacedItemsRemoveItems = (
    _products: {
        product?: ApiProduct;
        productId: string;
        variantId: string | undefined;
        quantity: number;
        replacedItem: boolean;
    }[],
    isUserMember: boolean,
) =>
    removeProductItems(
        _products.map((item) => ({
            product: item.product,
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity,
            replacedItem: item.replacedItem,
        })),
        { removalActionType: 'lost from cart', list: REPLACED_ITEM_LIST_NAME, isUserMember },
    );

const miniCartRecipeAccordionClick = (newState: 'collapse' | 'expand', recipeName: string) => {
    const event: MiniCartRecipeAccordionClick = {
        event: 'minicart_recipe_accordion_click',
        recipe_name: recipeName.toLowerCase(),
        type: newState,
    };

    sendGA4Event(event);
};

const cartRecipeSubstituteTriggerClick = (param: {
    productId: string;
    productName: string;
    recipeName: string;
}) => {
    const event: CartRecipeSubstituteTriggerClick = {
        event: 'cart_change_product_click',
        item_id: param.productId,
        item_name: param.productName.toLowerCase(),
        recipe_name: param.recipeName.toLowerCase(),
        button_text: 'byt vara',
    };

    sendGA4Event(event);
};

const modifyCartItems = async (
    inputProductData: {
        index?: number;
        productId: string;
        variantId: string | undefined;
        newQuantity: number;
        previousQuantity: number;
        replacedItem?: string; // the id of the product that replaces this item (for product substitute cases)
    }[],
    storeId: string,
    analyticsOptions: ModifyCartAnalyticsOptions,
) => {
    const productsWithVariants = await getGaProductsWithVariantsMap(inputProductData, storeId);

    // split products to add and remove, based on values of newQuantity and previousQuantity
    const { productsToAdd, productsToRemove } = inputProductData.reduce(
        (acc, { productId, variantId, previousQuantity, newQuantity, index, replacedItem }) => {
            const product = productsWithVariants.get(
                getGaMinimalProductKey({ productId, variantId }),
            );

            if (newQuantity > previousQuantity) {
                if (!product) return acc;
                return {
                    ...acc,
                    productsToAdd: [
                        ...acc.productsToAdd,
                        {
                            ...product,
                            selectedVariantId: variantId,
                            index,
                            quantity: newQuantity - previousQuantity,
                            replacedItem,
                        },
                    ],
                };
            }

            return {
                ...acc,
                productsToRemove: [
                    ...acc.productsToRemove,
                    {
                        productId,
                        product,
                        selectedVariantId: variantId,
                        index,
                        quantity: previousQuantity - newQuantity,
                        replacedItem: replacedItem !== undefined ? !!replacedItem : undefined,
                    },
                ],
            };
        },
        {
            productsToAdd: [] as (ApiProduct & {
                quantity: number;
                index?: number;
                selectedVariantId?: string;
                replacedItem?: string;
            })[],
            productsToRemove: [] as {
                productId: string;
                product?: ApiProduct;
                quantity: number;
                selectedVariantId?: string;
                replacedItem?: boolean;
            }[],
        },
    );
    if (productsToAdd.length) {
        addProductItems(analyticsOptions)(productsToAdd);
    }

    if (productsToRemove.length) {
        removeProductItems(productsToRemove, analyticsOptions);
    }
};

export interface OrderStatusAnalyticsOptions {
    order_status?: string;
}

export interface RecipeAnalyticsOptions {
    recipe_list_id?: string;
    recipe_list_name?: string;
    recipe_type?: string; // Send only one category. If multiple categories are selected in "Create Your Own Menu," send only "custom" (NOT "familjefavoriter|prisvärt|mer smak").
    recipe_origin?: 'weekly menu' | 'recipes';
}

// custom data sent with every modifyCartRequest, for example additional recipe data
export interface ModifyCartAnalyticsOptions
    extends AnalyticsListOptions, RecipeAnalyticsOptions, OrderStatusAnalyticsOptions {
    isUserMember: boolean;
    removalActionType?: RemoveFromCartType;
}

const addToCart = (
    _products: {
        productId: string;
        variantId: string | undefined;
        quantity: number;
    }[],
    storeId: string,
    analyticsOptions: ModifyCartAnalyticsOptions,
) =>
    modifyCartItems(
        _products.map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            previousQuantity: 0,
            newQuantity: item.quantity,
        })),
        storeId,
        analyticsOptions,
    );

const removeFromCart = (
    _products: {
        productId: string;
        variantId: string | undefined;
        quantity: number;
        replacedItem?: boolean;
    }[],
    storeId: string,
    analyticsOptions: ModifyCartAnalyticsOptions,
) => {
    modifyCartItems(
        _products.map((item) => ({
            productId: item.productId,
            variantId: item.variantId,
            previousQuantity: item.quantity,
            newQuantity: 0,
        })),
        storeId,
        analyticsOptions,
    );
};

export const changeCartItems = (
    previousProducts: {
        productId: string;
        variantId: string | undefined;
        quantity: number;
    }[],
    currentProducts: {
        productId: string;
        variantId: string | undefined;
        quantity: number;
    }[],
    storeId: string,
    analyticsOptions: ModifyCartAnalyticsOptions,
) => {
    const changedItems = getGaChangedItems(previousProducts, currentProducts);

    modifyCartItems(changedItems, storeId, analyticsOptions);
};

const highAmountInCartImpression = () => {
    const event: HighAmountInCartImpressionEvent = {
        event: 'impression',
        event_key: 'impression_above_50_single_good_in_cart',
        type: 'warning above 50 of single good in cart',
    };

    sendGA4Event(event);
};

export default {
    changeCartItems: failSilently(changeCartItems),
    modifyCartItems: failSilently(modifyCartItems),
    addToCart: failSilently(addToCart),
    removeFromCart: failSilently(removeFromCart),

    highAmountInCartImpression: failSilently(highAmountInCartImpression),
    viewCart: failSilently(viewCart),
    addToWishlist: failSilently(addToWishlist),
    replacedItems: {
        addItems: failSilently(replacedItemsAddItems),
        removeItems: failSilently(replacedItemsRemoveItems),
    },
    miniCartRecipeAccordionClick: failSilently(miniCartRecipeAccordionClick),
    cartRecipeSubstituteTriggerClick: failSilently(cartRecipeSubstituteTriggerClick),
    mergeCartInteraction: failSilently(mergeCartInteraction),
    displayMergeOptions: failSilently(displayMergeOptions),
    assortmentChangeWarning: failSilently(assortmentChangeWarning),
};
